import React from 'react';
import { Box, Typography, Button, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { Refresh as RefreshIcon, FiberManualRecord as DotIcon } from '@mui/icons-material';

interface AutoRefreshConfig {
  active: boolean;
  disabled?: boolean;
  onToggle: () => void;
}

interface ViewToolbarProps {
  /** Left-aligned controls (e.g. a time-range selector). */
  children?: React.ReactNode;
  count: React.ReactNode;
  autoRefresh?: AutoRefreshConfig;
  onRefresh: () => void;
  loading?: boolean;
}

/** Shared list toolbar: left controls, a right-aligned count, an optional
 *  auto-refresh toggle, and a manual refresh — kept identical across views. */
const ViewToolbar: React.FC<ViewToolbarProps> = ({ children, count, autoRefresh, onRefresh, loading }) => (
  <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, minHeight: 56 }}>
    {children}
    <Box sx={{ flex: 1 }} />
    <Typography variant="body2" color="text.secondary">{count}</Typography>
    {autoRefresh && (
      <Tooltip title={
        autoRefresh.disabled ? 'Live updates are unavailable for a custom range'
          : autoRefresh.active ? 'Live — new events stream in automatically. Click to stop.'
            : 'Go live — stream new events as they arrive'
      }>
        <span>
          <Button
            size="small"
            onClick={autoRefresh.onToggle}
            disabled={autoRefresh.disabled}
            variant={autoRefresh.active ? 'contained' : 'outlined'}
            color={autoRefresh.active ? 'success' : 'inherit'}
            startIcon={
              <DotIcon sx={{
                fontSize: 10,
                ...(autoRefresh.active
                  ? { animation: 'pulse 1.5s ease-in-out infinite', '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.25 } } }
                  : {}),
              }} />
            }
            sx={{ minWidth: 84, textTransform: 'none' }}
          >
            Live
          </Button>
        </span>
      </Tooltip>
    )}
    <Tooltip title="Refresh">
      <span>
        <IconButton onClick={onRefresh} size="small" disabled={loading} sx={{ color: 'text.secondary' }}>
          {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
        </IconButton>
      </span>
    </Tooltip>
  </Box>
);

export default ViewToolbar;
